"@apollo/client";
import {
  Breadcrumbs,
  BreadcrumbsItem,
  EditableLabel,
  IconButton,
  Switch,
} from "@joor/design-system";
import Mixpanel from "mixpanel-browser";
import {
  useQueryData,
  BreadCrumbNavData,
} from "./queries/WorkspaceOrderHeader.queries";
import styled from "styled-components/macro";
import { workspaceOverviewRoute } from "components/Workspace/paths";
import { fromGlobalId } from "lib/Relay";
import { generatePath } from "react-router-dom";
import createAbsolutePath from "createAbsolutePath";
import { ToClassicButton } from "ui/Button";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useWorkspaceAssistantEnabled } from "components/Store/workspaceAssistantEnabled";
import { useGlobalAssortmentId } from "components/Routes";
import { useHasWorkspaceValidationRequirements } from "./queries/WorkspaceOrderValidationRequirements";
import useProductSectionState from "AssortmentOverview/ProductSection/state/productSection/useProductSectionState";
import CreateOrderModal from "./CreateOrder/CreateOrderModal.component";
import { useState } from "react";
import { useWorkspaceId } from "components/Workspace/Routes";
import {
  useUpdateOrderName,
  useUpdateWorkspaceName,
} from "./mutations/updateWorkspaceOrderNames";
import { containsAlphaNumeric } from "./utils";

const StyledHeader = styled.header`
  position: sticky;
  align-items: center;
  top: 0px;
  z-index: 29;
  display: flex;
  height: 56px;
  background-color: var(--color-neutral-000-full);
  border-bottom: 1px solid var(--color-primary-100);
  padding: 0px 20px;
  justify-content: space-between;
  margin-top: 50px;
`;

const StyledBreadcrumbsItem = styled(BreadcrumbsItem)`
  align-items: center;
  & > span {
    color: var(--color-primary-800);
    font-weight: var(--font-weight-medium);
    font-size: var(--size-font-medium);
  }
  & > a {
    font-weight: var(--font-weight-medium);
    font-size: var(--size-font-medium);
  }
`;

const StyledHubIcon = styled(IconButton)`
  height: 20px;
  width: 20px;
  padding: 3px;
  margin-right: 5px;
  & > span {
    display: flex;
    margin: 0px;
    align-items: center;
    justify-content: center;
  }
  & > span > svg {
    display: block;
    width: 15px;
  }
`;

const StyledBreadcrumbs = styled(Breadcrumbs)`
  position: sticky;
  align-items: center;
  top: 0px;
  z-index: 29;
  display: flex;
  height: 56px;
`;

const RightSide = styled.div`
  display: flex;
  gap: 20px;
  align-items: center;
`;

const StyledSwitch = styled(Switch)`
  font-size: var(--size-font-small);
  padding-right: 6px;
`;

const StyleWrapper = styled.div`
  padding-right: 20px;
`;

type EditableBreadcrumbsItemProps = {
  href?: string;
  isEditable: boolean;
  value: string;
  onSubmit: (value: string) => Promise<void>;
};

const EditableBreadcrumbsItem = (props: EditableBreadcrumbsItemProps) => {
  const [newValue, setNewValue] = useState(props.value);
  const [errorMessage, setErrorMessage] = useState<string | undefined>(
    undefined
  );
  const submit = (newValue: string, oldValue: string): void => {
    const trimmedNewValue = newValue.trim();
    if (!containsAlphaNumeric(trimmedNewValue)) {
      setErrorMessage("Please enter at least one alphanumeric character.");
      throw new Error();
    } else if (trimmedNewValue.length > 100) {
      setErrorMessage("Maximum number of characters is 100");
      throw new Error();
    } else if (trimmedNewValue !== oldValue) {
      props.onSubmit(newValue);
      setErrorMessage(undefined);
    }
  };

  return (
    <StyledBreadcrumbsItem href={props.href} isCurrentPage={props.isEditable}>
      {props.isEditable ? (
        <EditableLabel
          errorMessage={errorMessage}
          onChange={setNewValue}
          onSubmit={(incoming) => submit(incoming, props.value)}
          value={newValue}
        >
          {props.value}
        </EditableLabel>
      ) : (
        props.value
      )}
    </StyledBreadcrumbsItem>
  );
};

type WorkspaceAssistantToggleProps = {
  view: HeaderPageView;
};
const WorkspaceAssistantToggle = ({ view }: WorkspaceAssistantToggleProps) => {
  const [workspaceAssistantEnabled, setWorkspaceAssistantEnabled] =
    useWorkspaceAssistantEnabled();
  const orderStates = useProductSectionState();

  return (
    <StyledSwitch
      id="workspace-assistant"
      onChange={() => {
        try {
          Mixpanel.track("Click Show Requirements");
        } catch (e) {}
        view === "workspace"
          ? setWorkspaceAssistantEnabled(!workspaceAssistantEnabled)
          : orderStates.enableWorkspaceAssistant(
              !orderStates.workspaceAssistantEnabled
            );
      }}
      checked={
        view === "workspace"
          ? workspaceAssistantEnabled
          : orderStates.workspaceAssistantEnabled
      }
      label="Show Requirements"
    />
  );
};

type VisibleModals = "createOrderModal" | null;

export type HeaderPageView = "workspace" | "order";

type WorkspaceOrderHeaderProps = {
  view: HeaderPageView;
};

const WorkspaceOrderHeader = ({ view }: WorkspaceOrderHeaderProps) => {
  const workspaceId = useWorkspaceId();
  const assortmentId = useGlobalAssortmentId();
  const data = useQueryData(workspaceId, assortmentId, view);
  const [visibleModal, setVisibleModal] = useState<VisibleModals>(null);
  const mutateWorkspaceName = useUpdateWorkspaceName(workspaceId);
  const mutateAssortmentName = useUpdateOrderName();
  const { removeBackToClassicButton } = useFlags();
  const buildBreadCrumbs = (data: BreadCrumbNavData) => {
    const workspacePath =
      data.workspaceId && data.assortmentName
        ? generatePath(workspaceOverviewRoute, fromGlobalId(data.workspaceId))
        : "#";
    return (
      <>
        <StyledBreadcrumbsItem>
          <StyledHubIcon
            iconName="hubs"
            onClick={() => window.open(createAbsolutePath("/ra/workspaces"))}
          />
        </StyledBreadcrumbsItem>
        {data.workspaceName && (
          <EditableBreadcrumbsItem
            href={`#${workspacePath}`}
            isEditable={data.assortmentName === undefined}
            value={data.workspaceName}
            onSubmit={mutateWorkspaceName}
          />
        )}
        {data.assortmentName && (
          <EditableBreadcrumbsItem
            isEditable
            value={data.assortmentName}
            onSubmit={mutateAssortmentName}
          />
        )}
      </>
    );
  };

  const showWorkspaceAssistantToggle = useHasWorkspaceValidationRequirements(
    workspaceId,
    assortmentId,
    view
  );

  return (
    <StyledHeader>
      <StyledBreadcrumbs data-testid="WorkspaceOrderHeader">
        {buildBreadCrumbs(data)}
      </StyledBreadcrumbs>
      <RightSide>
        {showWorkspaceAssistantToggle && (
          <WorkspaceAssistantToggle view={view} />
        )}
        {view === "workspace" && (
          <CreateOrderModal
            isOpen={visibleModal === "createOrderModal"}
            toggleModal={() => {
              setVisibleModal((visibleModal) =>
                visibleModal === "createOrderModal" ? null : "createOrderModal"
              );
            }}
          />
        )}
        {!removeBackToClassicButton && view !== "workspace" && (
          <StyleWrapper>
            <ToClassicButton />
          </StyleWrapper>
        )}
      </RightSide>
    </StyledHeader>
  );
};

export default WorkspaceOrderHeader;
